<template>
  <v-container id="lists-table" fluid>
    <v-card outlined>
      <v-data-table
        :headers="headers"
        :items="lists"
        :options.sync="options"
        :server-items-length="totalLists"
        :footer-props="footerProps"
        :loading="loading"
        :loading-text="$t('list.loadingText')"
        :no-data-text="$t('list.noList')"
        mobile-breakpoint="768"
        item-key="id"
        :header-props="headerProps"
      >
        <template v-slot:top>
          <v-toolbar flat>
            <v-spacer />
            <v-btn color="primary" class="mr-1" outlined @click="showModeDialog = true" :loading="loading" :disabled="loading">
              <v-icon>mdi-plus</v-icon>
            </v-btn>
            <v-btn color="primary" outlined @click="reloadList()" :loading="loading" :disabled="loading">
              <v-icon>mdi-refresh</v-icon>
            </v-btn>
          </v-toolbar>
        </template>
        <template v-slot:item.values="{ item }">
          {{ item.values.length }}
        </template>
        <template v-slot:item.last_execution_date="{ item }">
          <span v-if="item.mode === 'DYNAMIC'">
            {{ item.last_execution_date | moment('L LT') }}
          </span>
        </template>
        <template v-slot:item.last_execution_status="{ item }">
          <v-tooltip v-if="item.mode === 'DYNAMIC' && item.last_execution_status" bottom>
            <template v-slot:activator="{ on }">
              <v-icon v-on="on" :color="getStatusColor(item.last_execution_status)" small>mdi-circle</v-icon>
            </template>
            <span>
              {{
                $te(`list.executionStatus.${item.last_execution_status}`)
                  ? $t(`list.executionStatus.${item.last_execution_status}`)
                  : item.last_execution_status
              }}
            </span>
          </v-tooltip>
        </template>
        <template v-slot:item.action="{ item }">
          <v-icon color="primary" class="mr-2" @click="editList(item)">mdi-square-edit-outline</v-icon>
          <v-icon color="error" @click="openDeleteListDialog(item.list_id)">mdi-delete</v-icon>
        </template>
      </v-data-table>
    </v-card>
    <confirmation-dialog
      v-if="showDeleteListDialog"
      v-model="showDeleteListDialog"
      :text="$t('list.deletionListDialogText')"
      :title="$t('list.deletionListDialogTitle')"
      @no="showDeleteListDialog = false"
      @yes="deleteList(selectedListId)"
    ></confirmation-dialog>
    <selection-dialog
      v-model="showModeDialog"
      :title="$t('list.selectModeTitle')"
      :confirm-disabled="!selectedMode"
      @close="closeDialog"
      @confirm="onModeSelected"
    >
      <v-select
        v-model="selectedMode"
        :items="listModeChoices"
        item-text="text"
        item-value="value"
        :label="$t('list.listType')"
        outlined
        dense
      />
    </selection-dialog>
  </v-container>
</template>

<script>
  import { EventBus } from '../../event-bus'
  import confirmationDialog from '../../components/ConfirmationDialog.vue'
  import selectionDialog from '@/components/dialogs/SelectionDialog.vue'
  import { ListService } from '@/services/listService'
  import { getStatusColor } from '../../helpers/helper'

  export default {
    name: 'ListsTable',
    components: {
      confirmationDialog,
      selectionDialog,
    },
    data() {
      return {
        lists: [],
        loading: false,
        options: {
          itemsPerPage: 15,
        },
        totalLists: 0,
        showDeleteListDialog: false,
        showModeDialog: false,
        selectedMode: null,
        selectedListId: undefined,
        listService: {},
      }
    },
    activated() {
      this.listService = new ListService(this.$auth.accessToken)
      if (this.$store.state.previousPath.split('/')[1] !== 'lists') {
        this.options.itemsPerPage = 15
        this.options.page = 1
        this.options.sortBy = []
        this.options.sortDesc = []
      } else {
        this.reloadList()
      }
    },
    watch: {
      options: {
        handler() {
          this.reloadList()
        },
      },
    },
    computed: {
      headers() {
        return [
          {
            text: this.$t('list.listId'),
            value: 'list_id',
            class: 'break-col',
            cellClass: 'break-col',
          },
          {
            text: this.$t('list.name'),
            value: 'name',
            class: 'wrap-col',
            cellClass: 'wrap-col',
          },
          {
            text: this.$t('list.Quantity'),
            value: 'values',
            width: '80px',
          },
          {
            text: this.$t('list.lastExecutionDate'),
            value: 'last_execution_date',
            sortable: false,
            width: '130px',
            class: 'wrap-header',
          },
          {
            text: this.$t('list.lastExecutionStatus'),
            value: 'last_execution_status',
            sortable: false,
            align: 'center',
            width: '100px',
          },
          {
            text: '',
            value: 'action',
            sortable: false,
            width: '80px',
            cellClass: 'nowrap-col',
          },
        ]
      },
      footerProps() {
        return {
          'items-per-page-options': [15, 30, 50],
          'items-per-page-text': this.$t('list.listsPerPage'),
        }
      },
      headerProps() {
        return {
          sortByText: this.$t('list.sortByText'),
        }
      },
      listModeChoices() {
        return [
          { value: 'static', text: this.$t('list.modeStatic') },
          { value: 'dynamic', text: this.$t('list.modeDynamic') },
        ]
      },
    },
    methods: {
      async list() {
        this.loading = true
        const res = await this.listService.list(this.options)
        if (res) {
          this.totalLists = res.nbLists
          this.lists = res.data

          if (this.totalLists < (this.options.page - 1) * this.options.itemsPerPage + 1) {
            const pageNumber = Math.floor(this.totalLists / this.options.itemsPerPage)
            this.options.page = pageNumber === 0 ? 1 : pageNumber
          }
        }
        this.loading = false
      },
      async delete(listId) {
        this.loading = true
        EventBus.$emit('displayGlobalOverlay', { display: true, message: this.$t('list.loadingText') })
        const res = await this.listService.delete(listId)
        if (res) {
          await this.reloadList()
        }
        EventBus.$emit('displayGlobalOverlay', { display: false })
        this.loading = false
      },
      async reloadList() {
        EventBus.$emit('displayGlobalOverlay', { display: true, message: this.$t('list.loadingText') })
        await this.$auth.isAuthenticated()
        EventBus.$emit('reLoadCounter')
        await this.list()
        EventBus.$emit('displayGlobalOverlay', { display: false })
      },
      closeDialog() {
        this.showModeDialog = false
        this.selectedMode = null
      },
      onModeSelected() {
        this.$router.push({ name: 'listAdd', query: { mode: this.selectedMode } })
        this.selectedMode = null
      },
      getStatusColor,
      editList(item) {
        this.$router.push(`/lists/${item.list_id}`)
      },
      openDeleteListDialog(listId) {
        this.selectedListId = listId
        this.showDeleteListDialog = true
      },
      async deleteList(listId) {
        await this.delete(listId)
        this.selectedListId = false
      },
    },
  }
</script>

<style scoped>
  ::v-deep .wrap-col,
  ::v-deep .wrap-col .v-data-table__mobile-row__cell {
    white-space: normal;
    overflow-wrap: break-word;
  }
  ::v-deep .break-col,
  ::v-deep .break-col .v-data-table__mobile-row__cell {
    white-space: normal;
    word-break: break-all;
  }
  @media (max-width: 767px) {
    ::v-deep .v-data-table__wrapper {
      overflow-x: hidden;
    }
    ::v-deep .v-data-table__mobile-row {
      max-width: 100%;
    }
    ::v-deep .v-data-table__mobile-row > td {
      max-width: 100%;
      overflow: hidden;
    }
    ::v-deep .v-data-table__mobile-row__cell {
      min-width: 0;
      max-width: 55%;
      overflow-wrap: anywhere;
      word-break: break-all;
      text-align: right;
    }
  }
  @media (min-width: 768px) {
    ::v-deep .v-data-table__wrapper > table {
      table-layout: fixed;
    }
    ::v-deep .wrap-col {
      max-width: 250px;
    }
    ::v-deep .break-col {
      max-width: 250px;
    }
    ::v-deep .wrap-header {
      white-space: normal;
    }
    ::v-deep .nowrap-col {
      white-space: nowrap;
    }
  }
</style>
